import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { getHoldingsSummary, readSavedHoldingsSummary } from "@/lib/api";
import { toast } from "sonner";
import { isNative, shareImageNative } from "@/lib/native";
import IncomeGoalProgress from "@/components/holdings/IncomeGoalProgress";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Wallet, RefreshCw, ArrowRight, Building2, Landmark, PiggyBank,
  DollarSign, Loader2, Link as LinkIcon, TrendingUp, TrendingDown,
  Share2,
} from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, YAxis } from "recharts";

// Compact USD formatter (e.g. $12,451.22). Handles null/undefined gracefully.
const fmt$ = (v) => (v == null || Number.isNaN(Number(v)))
  ? "—"
  : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Abbreviated USD (e.g. $12.4k / $2.1M) for chips that must stay narrow.
const fmt$k = (v) => {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const n = Math.abs(Number(v));
  if (n >= 1_000_000) return `$${(Number(v) / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(Number(v) / 1_000).toFixed(1)}k`;
  return `$${Math.round(Number(v)).toLocaleString()}`;
};
const relTime = (iso) => {
  if (!iso) return null;
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const s = Math.max(0, Math.round((now - then) / 1000));
    if (s < 60) return "just now";
    const m = Math.round(s / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h} hr ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
  } catch { return null; }
};

// Per-account-type icon so users can visually parse Roth vs Taxable vs 401k.
const TYPE_ICONS = {
  "Taxable": Landmark,
  "Roth IRA": PiggyBank,
  "Traditional IRA": PiggyBank,
  "401k": Building2,
  "HSA": Building2,
  "Other": Wallet,
};

/**
 * TotalHoldingsCard — hero card on My Dashboard showing the user's *combined*
 * account total (broker-synced + manual). Only renders when the user has at
 * least one account. Emphasizes the SnapTrade-synced portion because those
 * numbers are broker-authoritative (positions_value + cash + buying_power).
 *
 * Data flow:
 *   1. /api/holdings/summary  — combined totals + per-account breakdown
 *   2. /api/snaptrade/status  — last sync timestamp + connection status
 *   3. /api/snaptrade/sync    — POST to trigger a background refresh
 */
export default function TotalHoldingsCard() {
  const nav = useNavigate();
  const [summary, setSummary] = useState(readSavedHoldingsSummary);
  const [snap, setSnap] = useState(null);
  const [history, setHistory] = useState(null); // { points: [...] }
  const [loading, setLoading] = useState(() => !readSavedHoldingsSummary());
  const [refreshing, setRefreshing] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const cardRef = useRef(null);

  const load = useCallback(async (force = false) => {
    try {
      const [sumRes, snapRes, histRes] = await Promise.allSettled([
        getHoldingsSummary(force),
        api.get("/snaptrade/status"),
        api.get("/holdings/history", { params: { days: 30 } }),
      ]);
      if (sumRes.status === "fulfilled") setSummary(sumRes.value.data);
      if (snapRes.status === "fulfilled") setSnap(snapRes.value.data);
      if (histRes.status === "fulfilled") setHistory(histRes.value.data);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Derive synced vs manual split from the per-account list. The backend
  // tags SnapTrade accounts with provider="snaptrade" and stamps
  // snap_total_value on each — so we can compute the synced sub-total
  // client-side without a second endpoint call.
  const split = useMemo(() => {
    if (!summary?.accounts?.length) return null;
    let syncedTotal = 0;
    let syncedCount = 0;
    let manualCount = 0;
    for (const a of summary.accounts) {
      if (a.provider === "snaptrade") {
        syncedCount += 1;
        syncedTotal += Number(a.snap_total_value || 0);
      } else {
        manualCount += 1;
      }
    }
    return { syncedTotal, syncedCount, manualCount };
  }, [summary]);

  // Sparkline series — daily total_value snapshots over the last 30 days.
  // The `/history` endpoint returns snapshots keyed by (user, portfolio_scope, date)
  // which are lazily upserted on every /summary call. Fresh users may have <2 points,
  // in which case we hide the sparkline entirely (below).
  const trend = useMemo(() => {
    const pts = history?.points || [];
    if (pts.length < 2) return null;
    const values = pts.map((p) => Number(p.total_value || 0));
    const first = values[0];
    const last = values[values.length - 1];
    const delta = last - first;
    const deltaPct = first > 0 ? (delta / first) * 100 : 0;
    const min = Math.min(...values);
    const max = Math.max(...values);
    // Add a tiny y-domain buffer so a totally flat series still renders as a
    // horizontal line rather than a NaN-scaled void.
    const pad = (max - min) * 0.15 || Math.max(1, last * 0.01);
    return {
      series: pts.map((p) => ({ date: p.date, value: Number(p.total_value || 0) })),
      delta,
      deltaPct,
      up: delta >= 0,
      domain: [min - pad, max + pad],
      firstDate: pts[0].date,
      lastDate: pts[pts.length - 1].date,
    };
  }, [history]);

  // Share as PNG — dynamically imports html2canvas (bundled with html2pdf.js)
  // and rasterizes the card. We swap the Share button for a spinner while
  // rendering to avoid it appearing in the exported image. Falls back to the
  // Web Share API on mobile so the OS sheet can send the image directly to
  // Messages / WhatsApp / Twitter.
  const doShare = async () => {
    if (!cardRef.current) return;
    setSharing(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#0F2A44",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      // Add a footer branding strip so the shared image reads as "from DPP"
      // even without the app chrome.
      const stamped = document.createElement("canvas");
      stamped.width = canvas.width;
      stamped.height = canvas.height + 88;
      const ctx = stamped.getContext("2d");
      ctx.fillStyle = "#0F2A44";
      ctx.fillRect(0, 0, stamped.width, stamped.height);
      ctx.drawImage(canvas, 0, 0);
      ctx.fillStyle = "#F59E0B";
      ctx.font = `bold ${28 * 2}px system-ui, -apple-system, sans-serif`;
      ctx.textBaseline = "middle";
      ctx.fillText("Dividend Portfolio Planner", 40, canvas.height + 44);
      ctx.fillStyle = "rgba(255,255,255,0.55)";
      ctx.font = `${18 * 2}px system-ui, -apple-system, sans-serif`;
      const url = "dividendportfolioplanner.com";
      ctx.fillText(url, stamped.width - ctx.measureText(url).width - 40, canvas.height + 44);

      const blob = await new Promise((res) => stamped.toBlob(res, "image/png", 0.95));
      const filename = `dpp-holdings-${new Date().toISOString().slice(0, 10)}.png`;

      if (isNative()) {
        await shareImageNative(blob, filename, { title: "My Dividend Portfolio", text: "Tracking my dividend income with DPP." });
        return;
      }
      // Web Share API path (mobile) — hand the file to the OS sheet.
      if (navigator.share && navigator.canShare) {
        try {
          const file = new File([blob], filename, { type: "image/png" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: "My Dividend Portfolio",
              text: "Tracking my dividend income with DPP.",
            });
            toast.success("Shared");
            return;
          }
        } catch (e) {
          if (e?.name === "AbortError") return; // user cancelled — silent
          // fall through to download
        }
      }
      // Desktop fallback — trigger a download.
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(dlUrl), 1000);
      toast.success("Image saved");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("share failed", e);
      toast.error("Could not create image");
    } finally {
      setSharing(false);
    }
  };

  const doSync = async () => {
    setSyncing(true);
    try {
      await api.post("/snaptrade/sync");
      toast.success("Sync started — refreshing in a moment…");
      // Poll status a couple times so the "last synced" chip updates
      // without a hard page reload.
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const s = await api.get("/snaptrade/status");
          const state = s.data?.item?.sync_state?.status;
          setSnap(s.data);
          if (state === "success" || state === "error" || tries >= 8) {
            await load(true);
            setSyncing(false);
            if (state === "success") toast.success("Broker data refreshed");
            else if (state === "error") toast.error("Sync failed — see Holdings page");
            return;
          }
        } catch { /* silent */ }
        setTimeout(poll, 2000);
      };
      setTimeout(poll, 2000);
    } catch (e) {
      setSyncing(false);
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 404) {
        toast.info("Connect a brokerage first");
        nav("/holdings");
      } else {
        toast.error(detail || "Could not start sync");
      }
    }
  };

  if (loading) {
    return <div className="mb-6 h-32 rounded-lg bg-muted animate-pulse" data-testid="total-holdings-loading" />;
  }
  // Hide the card entirely when the user has no accounts — the existing
  // "Set up holdings" empty state in HoldingsPanel already covers this.
  if (!summary || summary.account_count === 0) return null;

  const totals = summary.totals || {};
  const combinedTotal = Number(summary.total_value || totals.total_value || 0);
  const positionsValueCombined = Number(totals.total_value_ex_cash || 0);
  const cash = Number(summary.cash_at_broker || totals.cash_at_broker || 0);
  const buyingPower = Number(summary.buying_power_at_broker || totals.buying_power_at_broker || 0);
  const monthlyIncome = Number(summary.forward_monthly_income || 0);

  const lastSyncIso = snap?.item?.sync_state?.completed_at || snap?.item?.last_sync_at;
  const syncStatus = snap?.item?.sync_state?.status;
  const isConnected = !!snap?.item;

  return (
    <Card
      ref={cardRef}
      data-testid="total-holdings-card"
      className="mb-6 overflow-hidden border-primary/20 bg-gradient-to-br from-primary/[0.06] via-transparent to-secondary/[0.04]"
    >
      <CardContent className="p-6">
        {/* ---- Headline row: giant total + sync controls ---- */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.25em] text-primary">
              <Wallet className="w-3.5 h-3.5" />
              Total Holdings
              {split?.syncedCount > 0 && (
                <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-primary">
                  <LinkIcon className="w-3 h-3" />
                  {split.syncedCount} synced
                </span>
              )}
              {split?.manualCount > 0 && (
                <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
                  {split.manualCount} manual
                </span>
              )}
              {refreshing && (
                <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-secondary/10 px-2 py-0.5 text-secondary" data-testid="total-holdings-updating">
                  <Loader2 className="w-3 h-3 animate-spin" /> Updating…
                </span>
              )}
            </div>
            <div
              className="serif text-4xl sm:text-5xl mt-2 tabular-nums text-foreground"
              data-testid="total-holdings-value"
              title="Combined value across every account — synced broker positions, manual entries, and broker cash."
            >
              {fmt$(combinedTotal)}
            </div>
            <div className="mt-1 text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
              <span>
                Combined across <b>{summary.account_count}</b> account{summary.account_count === 1 ? "" : "s"} · <b>{summary.position_count}</b> position{summary.position_count === 1 ? "" : "s"}
              </span>
              {monthlyIncome > 0 && (
                <span className="inline-flex items-center gap-1">
                  · <TrendingUp className="inline w-3 h-3 text-secondary" />
                  <span className="text-secondary tabular-nums">{fmt$(monthlyIncome)}</span>/mo est. income
                </span>
              )}
              {trend && (
                <span
                  className={`inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 ${
                    trend.up
                      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                      : "bg-rose-500/10 text-rose-600 dark:text-rose-400"
                  }`}
                  data-testid="total-holdings-trend"
                  title={`Change from ${trend.firstDate} to ${trend.lastDate}`}
                >
                  {trend.up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {trend.up ? "+" : ""}{fmt$(trend.delta)}
                  <span className="opacity-70">({trend.up ? "+" : ""}{trend.deltaPct.toFixed(2)}%)</span>
                  <span className="opacity-60 normal-case tracking-normal">30d</span>
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end gap-2 shrink-0">
            <div className="flex items-center gap-2">
              {/* Share as PNG — hidden while rendering to keep the button out of the snapshot */}
              {!sharing && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-full h-8 px-2.5"
                  onClick={doShare}
                  data-testid="total-holdings-share"
                  aria-label="Share as image"
                  title="Save as a branded PNG image"
                >
                  <Share2 className="w-3.5 h-3.5" />
                </Button>
              )}
              {sharing && (
                <div className="h-8 px-2.5 inline-flex items-center text-muted-foreground">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                </div>
              )}
              {isConnected ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={doSync}
                  disabled={syncing || syncStatus === "pending"}
                  data-testid="total-holdings-sync"
                >
                  {(syncing || syncStatus === "pending") ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Syncing…</>
                  ) : (
                    <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Sync now</>
                  )}
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="rounded-full"
                  onClick={() => nav("/holdings")}
                  data-testid="total-holdings-connect"
                >
                  <LinkIcon className="w-3.5 h-3.5 mr-1.5" /> Link brokerage
                </Button>
              )}
            </div>
            {lastSyncIso && (
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Synced {relTime(lastSyncIso)}
              </div>
            )}
          </div>
        </div>

        <IncomeGoalProgress className="mt-4" testId="dashboard-income-goal" />

        {/* ---- 30-day value sparkline ---- */}
        {trend && (
          <div className="mt-4 h-16 -mx-2" data-testid="total-holdings-sparkline">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend.series} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                <defs>
                  <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={trend.up ? "#10B981" : "#F43F5E"} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={trend.up ? "#10B981" : "#F43F5E"} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <YAxis hide domain={trend.domain} />
                <Tooltip
                  cursor={{ stroke: "var(--muted-foreground)", strokeOpacity: 0.4, strokeDasharray: "2 2" }}
                  contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 11, padding: "6px 10px" }}
                  formatter={(v) => [fmt$(v), "Value"]}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.date || ""}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={trend.up ? "#10B981" : "#F43F5E"}
                  strokeWidth={2}
                  fill="url(#spark-fill)"
                  isAnimationActive={false}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* ---- Metric strip: Positions · Cash · Buying Power · Synced sub-total ---- */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricTile
            testId="total-positions"
            icon={<TrendingUp className="w-3.5 h-3.5" />}
            label="Positions Value"
            value={fmt$(positionsValueCombined)}
          />
          <MetricTile
            testId="total-cash"
            icon={<DollarSign className="w-3.5 h-3.5" />}
            label="Cash at Broker"
            value={fmt$(cash)}
            muted={cash === 0}
          />
          <MetricTile
            testId="total-buying-power"
            icon={<Wallet className="w-3.5 h-3.5" />}
            label="Buying Power"
            value={fmt$(buyingPower)}
            muted={buyingPower === 0}
          />
          <MetricTile
            testId="total-synced"
            icon={<LinkIcon className="w-3.5 h-3.5" />}
            label={split?.syncedCount ? "Synced Total" : "Synced"}
            value={split?.syncedCount ? fmt$(split.syncedTotal) : "—"}
            muted={!split?.syncedCount}
            tone="secondary"
          />
        </div>

        {/* ---- Per-account chip strip: "Robinhood Roth $12.4k · Fidelity Taxable $8.1k" ---- */}
        {summary.accounts?.length > 0 && (
          <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="total-holdings-accounts">
            {summary.accounts.slice(0, 6).map((a) => {
              const Icon = TYPE_ICONS[a.type] || Wallet;
              const isSynced = a.provider === "snaptrade";
              const val = isSynced ? a.snap_total_value : null;
              return (
                <button
                  key={a.id}
                  onClick={() => nav("/holdings")}
                  className={`group inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${
                    isSynced
                      ? "border-primary/25 bg-primary/[0.04] hover:bg-primary/10"
                      : "border-border bg-card hover:bg-muted"
                  }`}
                  title={`${a.name} (${a.type})${isSynced ? " · SnapTrade synced" : " · Manual"}`}
                  data-testid={`total-holdings-account-${a.id}`}
                >
                  <Icon className={`w-3.5 h-3.5 ${isSynced ? "text-primary" : "text-muted-foreground"}`} />
                  <span className="truncate max-w-[140px]">{a.name}</span>
                  {val != null && (
                    <span className="mono text-[11px] tabular-nums text-foreground/80 border-l border-border/60 pl-1.5 ml-0.5">
                      {fmt$k(val)}
                    </span>
                  )}
                  {isSynced && (
                    <LinkIcon className="w-3 h-3 text-primary/70 opacity-0 group-hover:opacity-100 transition-opacity" />
                  )}
                </button>
              );
            })}
            {summary.accounts.length > 6 && (
              <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                +{summary.accounts.length - 6} more
              </span>
            )}
            <button
              onClick={() => nav("/holdings")}
              className="ml-auto mono text-[10px] uppercase tracking-widest text-secondary hover:text-primary transition-colors inline-flex items-center gap-1"
              data-testid="total-holdings-open"
            >
              Open tracker <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MetricTile({ icon, label, value, tone, muted, testId }) {
  const toneCls = tone === "secondary" ? "text-secondary" : "text-foreground";
  return (
    <div
      className={`rounded-md border border-border/70 bg-card/60 backdrop-blur-sm px-3 py-2.5 ${muted ? "opacity-70" : ""}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-1 mono text-[9px] uppercase tracking-widest text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className={`serif text-lg mt-1 tabular-nums ${toneCls}`}>{value}</div>
    </div>
  );
}
